import type { Car } from '@multirx/shared';
import {
  clearCar3dPreviewSnapshotCache,
  mountCar3dPreview,
  type Car3dPreviewHandle,
} from '../cars/car3dPreview.js';
import { buildGarageHash } from '../garage/garageHash.js';
import { buildFeaturedCarCardModel, featuredCarCardInnerHtml } from './featuredCarCardModel.js';
import { pickNextFeaturedPilotId } from './featuredPilotPick.js';
import { playFeaturedPilotTransition } from './featuredPilotTransition.js';

export const FEATURED_CAR_ROTATE_MS = 4_000;

export type FeaturedCarCardHandle = {
  dispose(): void;
};

type MountFeaturedCarCardOptions = {
  cars: readonly Car[];
  rotateMs?: number;
};

function isDashboardViewActive(): boolean {
  return Boolean(document.getElementById('view-dashboard')?.classList.contains('is-active'));
}

export function mountFeaturedCarCard(
  host: HTMLElement,
  options: MountFeaturedCarCardOptions
): FeaturedCarCardHandle {
  const rotateMs = options.rotateMs ?? FEATURED_CAR_ROTATE_MS;
  const carsById = new Map(
    options.cars.filter((c): c is Car & { id: number } => c.id != null).map((c) => [c.id, c])
  );
  const carIds = [...carsById.keys()];

  clearCar3dPreviewSnapshotCache();

  host.className = 'card-glow-wrap card-glow-wrap--featured-car';
  host.innerHTML = `
    <a href="#garage/voitures" class="card-glow card-glow--preview featured-car-card" data-featured-car-link>
      <p class="featured-car-loading">Chargement des voitures…</p>
    </a>`;

  const link = host.querySelector<HTMLAnchorElement>('[data-featured-car-link]');
  if (!link || carIds.length === 0) {
    if (link) {
      link.innerHTML = `<p class="featured-car-empty">Aucune voiture à présenter pour l’instant.</p>`;
    }
    return { dispose() {} };
  }

  let disposed = false;
  let lastId: number | null = null;
  let generation = 0;
  let rotating = false;
  let preview: Car3dPreviewHandle | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const disposePreview = (): void => {
    preview?.dispose();
    preview = null;
  };

  const paint = (carId: number): void => {
    const gen = ++generation;
    const car = carsById.get(carId);
    if (!car) return;

    const model = buildFeaturedCarCardModel(car);
    if (disposed || gen !== generation) return;

    disposePreview();
    link.innerHTML = featuredCarCardInnerHtml(model);
    link.dataset.carId = String(carId);
    link.href = `#${buildGarageHash({ category: 'voitures', id: String(carId) })}`;

    const car3dEl = link.querySelector<HTMLElement>('[data-featured-car-3d]');
    if (car3dEl && model.modelUrl) {
      preview = mountCar3dPreview(car3dEl, model.modelUrl, undefined, { snapshot: true });
      car3dEl.classList.add('is-ready');
    }

    lastId = carId;
  };

  const rotate = (): void => {
    if (disposed || rotating || document.hidden || !isDashboardViewActive()) return;
    const nextId = pickNextFeaturedPilotId(carIds, lastId);
    if (nextId == null) return;
    if (lastId == null) {
      paint(nextId);
      return;
    }
    rotating = true;
    void playFeaturedPilotTransition(host, () => {
      paint(nextId);
    }).finally(() => {
      rotating = false;
    });
  };

  const onClick = (e: MouseEvent): void => {
    e.preventDefault();
    const id = Number(link.dataset.carId);
    if (Number.isFinite(id) && id > 0) {
      window.location.hash = buildGarageHash({ category: 'voitures', id: String(id) });
      return;
    }
    window.location.hash = 'garage/voitures';
  };

  const onVisibility = (): void => {
    if (!document.hidden && isDashboardViewActive() && lastId == null) {
      rotate();
    }
  };

  const onHash = (): void => {
    if (isDashboardViewActive() && lastId == null) rotate();
  };

  link.addEventListener('click', onClick);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('hashchange', onHash);

  const firstId = pickNextFeaturedPilotId(carIds, null);
  if (firstId != null) paint(firstId);

  timer = setInterval(rotate, rotateMs);

  return {
    dispose(): void {
      disposed = true;
      if (timer != null) clearInterval(timer);
      timer = null;
      disposePreview();
      link.removeEventListener('click', onClick);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('hashchange', onHash);
    },
  };
}
